import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Activity, BrainCircuit, ChevronDown, ChevronUp, CircleDollarSign, LogOut, RefreshCw,
  Search, Sparkles, TrendingUp, UserCheck, Users,
} from 'lucide-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie,
  PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { toast } from 'sonner';
import { API } from '../App';
import ThemeToggle from '../components/ThemeToggle';

const COLORS = ['#D4AF37', '#8B5CF6', '#22C55E', '#38BDF8', '#F97316'];

const MetricCard = ({ icon: Icon, label, value, detail, accent = '#D4AF37' }) => (
  <div className="admin-panel rounded-2xl p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</p>
        <p className="mt-3 text-3xl font-medium tracking-tight text-zinc-100">{value}</p>
        <p className="mt-2 text-sm text-zinc-500">{detail}</p>
      </div>
      <div className="rounded-xl p-3" style={{ color: accent, backgroundColor: `${accent}18` }}>
        <Icon className="h-5 w-5" strokeWidth={1.7} />
      </div>
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#111114] px-3 py-2 shadow-xl">
      <p className="mb-1 text-xs text-zinc-500">{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey || item.name} className="text-sm" style={{ color: item.color }}>
          {item.name}: {item.dataKey === 'revenue' ? `$${Number(item.value).toFixed(2)}` : item.value}
        </p>
      ))}
    </div>
  );
};

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('weekly');
  const [showUsers, setShowUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const loadAnalytics = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) navigate('/dashboard');
      else toast.error('Unable to load admin analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadAnalytics(true);
    const interval = window.setInterval(() => loadAnalytics(true), 60000);
    return () => window.clearInterval(interval);
  }, [loadAnalytics]);

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#070709] grid place-items-center text-[#D4AF37]">Loading live intelligence...</div>;
  }

  const kpis = data?.kpis || {};
  const trend = (data?.reports?.[reportPeriod] || []).map((item) => ({
    ...item,
    label: format(
      new Date(`${item.period}T00:00:00`),
      reportPeriod === 'monthly' ? 'MMM yy' : reportPeriod === 'weekly' ? "MMM d" : 'MMM d'
    ),
  }));
  const reportCopy = {
    daily: 'Daily performance, trailing 30 days',
    weekly: 'Weekly performance, trailing 12 weeks',
    monthly: 'Monthly performance, trailing 12 months',
  };
  const filteredUsers = (data?.user_details || []).filter((item) =>
    item.email?.toLowerCase().includes(userSearch.trim().toLowerCase())
  );

  return (
    <div className="admin-dashboard min-h-screen bg-[#070709] text-zinc-100">
      <nav className="border-b border-white/10 bg-[#09090b]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-5 py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-2">
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="font-heading text-xl leading-none">Seatask Intelligence</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500">AI BI Command Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <span className="hidden text-sm text-zinc-500 md:block">{user?.email}</span>
            <button onClick={handleLogout} className="rounded-lg p-2 text-zinc-500 hover:bg-white/5 hover:text-white" aria-label="Log out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-[1500px] px-5 py-8 lg:px-8">
        <header className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> Live operational data
            </div>
            <h1 className="font-heading text-4xl font-medium tracking-tight md:text-5xl">Business overview</h1>
            <p className="mt-2 max-w-2xl text-zinc-500">Monitor audience growth, reading demand, AI delivery quality, and commercial performance across Seatask Arcane AI.</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-right text-xs text-zinc-600">
              Last updated<br />{data?.generated_at ? format(new Date(data.generated_at), 'MMM d, yyyy HH:mm:ss') : 'Unavailable'}
            </p>
            <button onClick={() => loadAnalytics()} disabled={refreshing} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm hover:border-[#D4AF37]/40 hover:text-[#D4AF37] disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard icon={Users} label="Total users" value={kpis.total_users?.toLocaleString()} detail={`${kpis.active_users_7d || 0} active in the last 7 days`} />
          <MetricCard icon={Activity} label="Reading volume" value={kpis.total_readings?.toLocaleString()} detail={`${kpis.readings_today || 0} created today`} accent="#8B5CF6" />
          <MetricCard icon={CircleDollarSign} label="Lifetime revenue" value={`$${Number(kpis.total_revenue || 0).toFixed(2)}`} detail={`$${Number(kpis.revenue_30d || 0).toFixed(2)} in the last 30 days`} accent="#22C55E" />
          <MetricCard icon={TrendingUp} label="Paid conversion" value={`${kpis.conversion_rate || 0}%`} detail={`${kpis.paid_customers || 0} paying customers`} accent="#38BDF8" />
        </section>

        <section className="mt-6 grid gap-6 xl:grid-cols-[1.7fr_1fr]">
          <div className="admin-panel rounded-2xl p-5 lg:p-6">
            <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <div><h2 className="text-lg font-medium">Performance report</h2><p className="text-sm text-zinc-500">{reportCopy[reportPeriod]}</p></div>
              <div className="flex rounded-lg border border-white/10 bg-black/20 p-1">
                {['daily', 'weekly', 'monthly'].map((period) => (
                  <button key={period} onClick={() => setReportPeriod(period)} className={`rounded-md px-3 py-1.5 text-xs capitalize transition-colors ${reportPeriod === period ? 'bg-[#D4AF37] text-black' : 'text-zinc-500 hover:text-white'}`}>
                    {period}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ left: -20, right: 8 }}>
                  <defs><linearGradient id="readingsFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#D4AF37" stopOpacity={0.35}/><stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
                  <XAxis dataKey="label" stroke="#52525b" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis stroke="#52525b" tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#71717a' }} />
                  <Area type="monotone" dataKey="readings" name="Readings" stroke="#D4AF37" fill="url(#readingsFill)" strokeWidth={2} />
                  <Area type="monotone" dataKey="registrations" name="New users" stroke="#8B5CF6" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="admin-panel rounded-2xl p-5 lg:p-6">
            <h2 className="text-lg font-medium">Reading portfolio</h2><p className="text-sm text-zinc-500">Daily, Single, Three, Five and Ten Card usage</p>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={data?.spread_distribution || []} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={3}>{(data?.spread_distribution || []).map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip content={<ChartTooltip />} /></PieChart></ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {(data?.spread_distribution || []).slice(0, 5).map((item, index) => <div key={item.name} className="flex items-center justify-between text-sm"><span className="flex items-center gap-2 text-zinc-400"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />{item.name}</span><span>{item.value}</span></div>)}
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.35fr]">
          <div className="admin-panel rounded-2xl p-5 lg:p-6">
            <div className="mb-5 flex items-start justify-between"><div><h2 className="text-lg font-medium">Revenue report</h2><p className="text-sm text-zinc-500">{reportCopy[reportPeriod]}</p></div><CircleDollarSign className="h-5 w-5 text-emerald-400" /></div>
            <div className="h-[260px]"><ResponsiveContainer width="100%" height="100%"><BarChart data={trend} margin={{ left: -20 }}><CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false}/><XAxis dataKey="label" stroke="#52525b" axisLine={false} tickLine={false} fontSize={11}/><YAxis stroke="#52525b" axisLine={false} tickLine={false} fontSize={11}/><Tooltip content={<ChartTooltip />}/><Bar dataKey="revenue" name="Revenue" fill="#22C55E" radius={[5,5,0,0]} /></BarChart></ResponsiveContainer></div>
          </div>

          <div className="admin-panel rounded-2xl p-5 lg:p-6">
            <div className="mb-5 flex items-center justify-between"><div><h2 className="text-lg font-medium">Recent activity</h2><p className="text-sm text-zinc-500">Latest customer reading events</p></div><UserCheck className="h-5 w-5 text-sky-400" /></div>
            <div className="divide-y divide-white/5">
              {(data?.recent_activity || []).map((item) => <div key={item.id} className="flex items-center gap-3 py-3"><div className="rounded-lg bg-violet-500/10 p-2 text-violet-400"><BrainCircuit className="h-4 w-4" /></div><div className="min-w-0 flex-1"><p className="truncate text-sm text-zinc-200">{item.email}</p><p className="text-xs text-zinc-600">Created a {item.detail} reading</p></div><div className="text-right"><span className={`text-[10px] uppercase tracking-wider ${item.ai_powered ? 'text-emerald-400' : 'text-amber-400'}`}>{item.ai_powered ? 'AI delivered' : 'Fallback'}</span><p className="mt-1 text-xs text-zinc-600">{item.created_at ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true }) : ''}</p></div></div>)}
              {!data?.recent_activity?.length && <p className="py-12 text-center text-sm text-zinc-600">No activity recorded yet.</p>}
            </div>
          </div>
        </section>

        <section className="admin-panel mt-6 rounded-2xl p-5 lg:p-6">
          <button onClick={() => setShowUsers((current) => !current)} className="flex w-full items-center justify-between gap-4 text-left">
            <div>
              <h2 className="text-lg font-medium">User detail report</h2>
              <p className="text-sm text-zinc-500">All registered users, activity, revenue and reading portfolio</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-sky-500/10 px-3 py-1 text-xs text-sky-400">{data?.user_details?.length || 0} users</span>
              {showUsers ? <ChevronUp className="h-5 w-5 text-zinc-500" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
            </div>
          </button>

          {showUsers && (
            <div className="mt-6">
              <div className="relative mb-4 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input value={userSearch} onChange={(event) => setUserSearch(event.target.value)} placeholder="Search user email" className="w-full rounded-lg border border-white/10 bg-black/20 py-2.5 pl-10 pr-4 text-sm text-zinc-200 outline-none focus:border-[#D4AF37]/50" />
              </div>
              <div className="overflow-x-auto rounded-xl border border-white/5">
                <table className="min-w-[1050px] w-full text-left text-sm">
                  <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-zinc-600">
                    <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Last activity</th><th className="px-4 py-3 text-center">Readings</th><th className="px-4 py-3 text-center">Orders</th><th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3">Reading portfolio</th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map((item) => (
                      <tr key={item.id} className="hover:bg-white/[0.02]">
                        <td className="px-4 py-3"><p className="text-zinc-200">{item.email}</p><span className={`text-[10px] uppercase tracking-wider ${item.is_active_7d ? 'text-emerald-400' : 'text-zinc-600'}`}>{item.is_active_7d ? 'Active' : 'Inactive 7d'}</span></td>
                        <td className="px-4 py-3 text-zinc-500">{item.joined_at ? format(new Date(item.joined_at), 'MMM d, yyyy') : '-'}</td>
                        <td className="px-4 py-3 text-zinc-500">{item.last_activity ? formatDistanceToNow(new Date(item.last_activity), { addSuffix: true }) : 'No readings'}</td>
                        <td className="px-4 py-3 text-center text-zinc-300">{item.reading_count}</td>
                        <td className="px-4 py-3 text-center text-zinc-300">{item.paid_orders}</td>
                        <td className="px-4 py-3 text-right text-emerald-400">${Number(item.revenue || 0).toFixed(2)}</td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1.5">{item.portfolio?.map((spread) => <span key={spread.type} title={spread.name} className="rounded bg-white/5 px-2 py-1 text-[11px] text-zinc-500">{spread.name}: <b className="text-zinc-300">{spread.value}</b></span>)}</div></td>
                      </tr>
                    ))}
                    {!filteredUsers.length && <tr><td colSpan="7" className="px-4 py-10 text-center text-zinc-600">No users match this search.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="admin-panel rounded-xl p-4"><p className="text-xs uppercase tracking-wider text-zinc-600">AI delivery health</p><p className="mt-2 text-2xl text-emerald-400">{kpis.ai_success_rate || 0}%</p></div>
          <div className="admin-panel rounded-xl p-4"><p className="text-xs uppercase tracking-wider text-zinc-600">Active audience</p><p className="mt-2 text-2xl text-sky-400">{kpis.active_users_7d || 0}</p></div>
          <div className="admin-panel rounded-xl p-4"><p className="text-xs uppercase tracking-wider text-zinc-600">System state</p><p className="mt-2 text-2xl text-[#D4AF37]">Operational</p></div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
